import {
  Box,
  Button,
  Flex,
  Heading,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  SourcingEventStatus,
  type SourcingEventV2Dto,
  useBeginEvaluationMutation,
  useListPublicBidsForEventQuery,
} from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { PublicBidStatusTag } from "./PublicBidStatusTag";

interface Props {
  event: SourcingEventV2Dto;
}

export const BidsTab = ({ event }: Props) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const eventId = event.id;
  const { data, isLoading, isError } = useListPublicBidsForEventQuery({ eventId });
  const bids = data?.bids ?? [];

  return (
    <Stack spacing="5">
      {event.status === SourcingEventStatus.Published && <BeginEvaluationPanel event={event} />}
      {isLoading ? (
        <Flex justify="center" py="16">
          <Spinner color="smPrimary" />
        </Flex>
      ) : isError ? (
        <Flex justify="center" py="16">
          <Text color="smError">{t("Could not load bids.")}</Text>
        </Flex>
      ) : bids.length === 0 ? (
        <Flex justify="center" py="16">
          <Text color="smMuted">{t("No bids delivered yet.")}</Text>
        </Flex>
      ) : (
        <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" overflow="hidden">
          <TableContainer>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>{t("Supplier")}</Th>
                  <Th>{t("Status")}</Th>
                  <Th>{t("Submitted")}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {bids.map((bid) => (
                  <Tr key={bid.id}>
                    <Td fontWeight="medium" color="smText">
                      {bid.ownerName || t("Unknown")}
                    </Td>
                    <Td>
                      <PublicBidStatusTag status={bid.status} size="sm" />
                    </Td>
                    <Td color="smMuted">{bid.submittedAt ? displayDate(bid.submittedAt) : "–"}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Stack>
  );
};

const BeginEvaluationPanel = ({ event }: { event: SourcingEventV2Dto }) => {
  const { t } = useTranslation();
  const { triggerSoft } = useApiError();
  const [beginEvaluation, { isLoading }] = useBeginEvaluationMutation();

  const deadline = event.eventData.bidSubmissionDeadline;
  const deadlinePassed = deadline ? new Date(deadline).getTime() <= Date.now() : false;

  const onBegin = async () => {
    try {
      await beginEvaluation({ eventId: event.id }).unwrap();
    } catch (e) {
      triggerSoft(e);
    }
  };

  return (
    <Flex
      align={{ base: "stretch", md: "center" }}
      justify="space-between"
      direction={{ base: "column", md: "row" }}
      gap="3"
      bg="smBackground"
      border="1px solid"
      borderColor="smBorder"
      rounded="xl"
      px="5"
      py="4"
    >
      <Box>
        <Heading size="sm" mb="1">
          {t("Begin evaluation")}
        </Heading>
        <Text fontSize="sm" color="smMuted">
          {deadlinePassed
            ? t("The submission deadline has passed. Open the delivered bids to start scoring.")
            : t("You can begin evaluation once the submission deadline has passed.")}
        </Text>
      </Box>
      <Button
        colorScheme="teal"
        onClick={onBegin}
        isLoading={isLoading}
        isDisabled={!deadlinePassed}
        flexShrink={0}
      >
        {t("Begin evaluation")}
      </Button>
    </Flex>
  );
};
